// ============================================================
// TERROR TCG — COMPLETE CARD DATABASE
// ============================================================

export const CARD_TYPES = {
  ESCENDANT: "Escendant",
  ESCLING: "Escling",
  TOME: "Tome",
  IMPERA: "Impera",
  UHLTI: "U'hlti",
  AFFINITY: "Affinity",
  CURIO: "Curio",
  PROPHECY: "Prophecy",
};

// ── THE TWO DESCRIPTORS (designer's ruling, 2026-08-25) ──────
// Every being carries two words, and they come from two families.
// For BEINGS:
//   ILK   — the TOP-LEFT stamp  — Humanoid / Beast / Machine / Dragon
//   SCHOOL — the TOP-RIGHT stamp — Space / Phantom / Dragon / Time
// These key rules.js `PRIMERAS.ilk` and `PRIMERAS.school` respectively,
// and now do so under their own names.
//
// ⚠ WHAT THIS FIXED. Until 2026-08-25 these two field names were
// INVERTED on every being row: the Family-A word sat under `school:`
// and the Family-B word under `ilk:`, exactly backwards from the
// stamp tables that consume them. Three separate files each carried a
// private swap helper to compensate (Shatterfield's `primeraWordFor`,
// NPC's `stampWordFor`, the Debugger's two-table search in
// `stampsOf`). All three are gone; the data tells the truth instead.
// The swap was behaviour-neutral because ilk↔school is perfectly 1:1
// across all 88 records that carry the pair — only four pairings
// exist (Humanoid/Space, Beast/Phantom, Machine/Time, Dragon/Dragon),
// 22 records each.
//
// For TOMES, CURIOS and other supports the same two words mean
// something else entirely: `ilk` is the SCHOOL (Omnia, Ritual,
// Contract, Nihil, Construction, Treasure, Ancient Sorcery, Shadow
// Gambling — plus the Curio tags "wears"/"ally"/"J'ynxi"), and it is
// stored as a plain string on the row, which is correct and untouched.
// Their SCHOOL is the broad Aenergy-Art term — Tome, U'hlti, Impera,
// Affinity — which is already the card's own `type`. See `schoolOf`
// at the bottom of this file: it is exposed as a derived accessor
// rather than duplicated onto 100+ rows, because a copied field is a
// field that can disagree with itself.
export const ILKS = {
  HUMANOID: "Humanoid",
  BEAST: "Beast",
  MACHINE: "Machine",
  DRAGON: "Dragon",
};

export const SCHOOLS = {
  SPACE: "Space",
  PHANTOM: "Phantom",
  DRAGON: "Dragon",
  TIME: "Time",
};

// ══════════════════════════════════════════════════════════════
// ✦ WHAT AN AFFINITY IS (caP'n, 2026-08-26)
// ══════════════════════════════════════════════════════════════
// He laid the four out explicitly, on the same three axes every other
// card in the game carries:
//
//   ILK        SCHOOL      NAME
//   Holy       Affinity   Y'Tyranuz
//   Evil       Affinity   X'aol
//   Awoken     Affinity   Enlightenment
//   J'ynxi     Affinity   Fiend or Faux
//
// So an Affinity's ILK is Holy / Evil / Awoken / J'ynxi — exactly the
// values below — its SCHOOL is literally "Affinity", the broad
// Aenergy-Art term the Axis Ruling already gives every support (see
// `schoolOf` at the foot of this file, which derives it from `type` and
// therefore already returns "Affinity" for these four), and its NAME is
// the card's own name.
//
// ⚠ TWO SPELLINGS WERE HELD OPEN FOR HIM AND ARE NOW SETTLED.
// caP'n, 2026-09-13, asked outright and answering in his own hand:
//
//   *"Y'Tyranuz (capital 'T' and 'z' no 's') Fiend or Faux (not
//   Friend)"*
//
// So the cards ship as **Y'Tyranuz** and **Fiend or Faux**. The two
// spellings that stood here for a month — Y'tyranus, and "Friend or
// Faux" with the extra r — are dead. THE SPELLING-CHANGE PROTOCOL runs
// the other way now: an editor "fixing" Y'Tyranuz back to Y'tyranus, or
// Fiend back to Friend, is deleting canon. Guarded in canon0826.

export const AFFINITY_TYPES = {
  HOLY: "Holy",
  EVIL: "Evil",
  AWOKEN: "Awoken",
  JYNXI: "J'ynxi",
};

// ══════════════════════════════════════════════════════════════
// 👑 THE SUPREME TITLES — RENAMED (caP'n, 2026-08-26)
// ══════════════════════════════════════════════════════════════
//   *"D'aeternuz = A'oeternis. A'eternuz = A'aeternis."*
//
// These are BROAD TERMS for beings born in the Unstable World,
// typically of Lord-level power, most of whom predate time as a
// concept — and the rank is technically ATTAINABLE, which is exactly
// why a player can reach it: a Supreme Being on the Shatterfield is a
// mortal climbing into that company, not a title borrowed from it.
//
// The rename is total — card flavour text, Prophecy requirements ("1
// A'oeternis or A'aeternis Space Being"), the Rulebook, the Tutorial,
// the motion books, the glossary and every title check that matched
// the old strings. V'igiluz and Jester were NOT renamed and are
// untouched; he named two, and only two.
export const SUPREME_TITLES = {
  HOLY: "A'aeternis",
  EVIL: "A'oeternis",
  AWOKEN: "V'igiluz",
  JYNXI: "Jester",
};

// ============================================================
// ESCLINGS (Base entities placed before the duel)
// ============================================================
export const ESCLINGS = [
  { id: "escling_space", name: "Space Escling", type: CARD_TYPES.ESCLING, ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended." },
  { id: "escling_phantom", name: "Phantom Escling", type: CARD_TYPES.ESCLING, ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended." },
  { id: "escling_dragon", name: "Dragon Escling", type: CARD_TYPES.ESCLING, ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended." },
  { id: "escling_time", name: "Time Escling", type: CARD_TYPES.ESCLING, ilk: ILKS.MACHINE, school: SCHOOLS.TIME, lyt: 1, flavorText: "An Escling — primordial essence waiting to be Transcended." },
];

// ============================================================
// ESCENDANTS (Beings)
// ============================================================

export const ESCENDANTS = [

  // ── HUMANOID / SPACE ──────────────────────────────────────
  // H-01 Line
  {
    id: "h01_beb", name: "H-01 Blue Giant", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these beings choose words as their first resort.",
  },
  {
    id: "h01_champion", name: "H-01 Quasar", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 2,
    lyt: 2, lyf: 7, deckLimit: 4,
    flavorText: "Self-proclaimed Sons of the Suns, these humans have superior suits capable of defending these soldiers against even Champion level threats.",
  },
  {
    id: "h01_lord", name: "H-01 Super Nova", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3,
    lyt: 9, lyf: 1, deckLimit: 3,
    flavorText: "Souls fused with technology; this is H-01's greatest achievement.",
  },

  // H-09 Line
  {
    id: "h09_beb", name: "H-09 Neutron Star", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 1,
    lyt: 5, lyf: 3, deckLimit: 5,
    flavorText: "A variant of the H-0 ilk, these beings tend to choose militancy as their first option.",
  },
  {
    id: "h09_champion", name: "H-09 Pulsar", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Self-proclaimed Generals of the stars, these humans wield Star level blasters that can decimate most Champions in seconds.",
  },
  {
    id: "h09_lord", name: "H-09 Hyper Nova", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3,
    lyt: 10, lyf: 0, deckLimit: 3,
    flavorText: "The stars bend to these beings' will; their technology is infused with their soul.",
  },

  // Xulan Line
  {
    id: "xulan_beb", name: "Xulan-Wanderer", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "A race of assassins that drift through the Void, in search of revenge.",
  },
  {
    id: "xulan_champion", name: "Xulan-Surgeon", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "Guardians of their weakest territories, they almost never experience battle.",
  },
  {
    id: "xulan_lord", name: "Xulan-Shadow", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3,
    lyt: 10, lyf: 0, deckLimit: 3,
    flavorText: "Hate reverts the Surgeons to what they once were before their galaxy was destroyed by H-01.",
  },

  // ── BEAST / PHANTOM ───────────────────────────────────────
  // Spectral Tiger Line
  {
    id: "spectral_tiger_beb", name: "Spectral Tiger Beb", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 1,
    lyt: 6, lyf: 2, deckLimit: 5,
    flavorText: "These spoiled kittens are found only in the Lofty Kingdoms of the Spirit Worlds.",
  },
  {
    id: "spectral_tiger_champion", name: "Spectral Tiger Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Prowling the Shadow Forest, these tigers have developed an insatiable appetite for their own class.",
  },
  {
    id: "spectral_tiger_lord", name: "Spectral Tiger Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "Kings of the Spirit Worlds, these beings find moral ground with only the most benevolent of A'eterni.",
  },

  // Shadow Fox Line
  {
    id: "shadow_fox_beb", name: "Shadow Fox Beb", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "Mischievous pups from birth, they are born at the Bottomless Depths of the darkest Shadow Spheres.",
  },
  {
    id: "shadow_fox_champion", name: "Shadow Fox Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "Skulking the Shadow Forest, these fox prey on those who seek it.",
  },
  {
    id: "shadow_fox_lord", name: "Shadow Fox Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3,
    lyt: 4, lyf: 6, deckLimit: 3,
    flavorText: "Rulers of Shadow Spheres, these beings tend to find loyalty amongst only the most malevolent of D'aeterni.",
  },

  // Inverted Serpent Line
  {
    id: "inverted_serpent_beb", name: "Inverted Serpent Beb", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "Although small, do not be fooled by their appearance; these snakes are protectors to the entrance of the Shadow Forest.",
  },
  {
    id: "inverted_serpent_champion", name: "Inverted Serpent Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "Guardians of the Shadow Forest, these beings relentlessly slither through the shadows.",
  },
  {
    id: "inverted_serpent_lord", name: "Inverted Serpent Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3,
    lyt: 6, lyf: 4, deckLimit: 3,
    flavorText: "The Shadow Forest was and will always be home to these beings; they will protect it at any cost.",
  },

  // ── MACHINE / TIME ────────────────────────────────────────
  // Z4Z Line
  {
    id: "z4z_beb", name: "Z4Z-Demon", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 1,
    lyt: 8, lyf: 0, deckLimit: 5,
    flavorText: "These demons are, so far, responsible for destroying 1% of the Unstable Worlds; they'll destroy anything for Khao.",
  },
  {
    id: "z4z_champion", name: "Z4Z-Wizard", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 2,
    lyt: 7, lyf: 2, deckLimit: 4,
    flavorText: "Time is a cog in this machine's design to be used for one purpose, although that purpose is unknown.",
  },
  {
    id: "z4z_lord", name: "Z4Z H'ehll's Sword", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3,
    lyt: 8, lyf: 2, deckLimit: 3,
    flavorText: "No one has ever been able to have a conversation with one of these beings.",
  },

  // DG-0 Line
  {
    id: "dg0_beb", name: "DG-0 Angel", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 1,
    lyt: 2, lyf: 6, deckLimit: 5,
    flavorText: "Through tremendous effort, these robots have, over time, healed 1% of dying beings in the Unstable World.",
  },
  {
    id: "dg0_champion", name: "DG-0 Farmer", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 2,
    lyt: 2, lyf: 7, deckLimit: 4,
    flavorText: "Farmers of Hope, these beings bring peace and prosperity, if you share their point of view, that is.",
  },
  {
    id: "dg0_lord", name: "DG-0 I'nfini's Shield", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3,
    lyt: 2, lyf: 8, deckLimit: 3,
    flavorText: "If seen, it's usually in response to an Apocalypse level situation.",
  },

  // Antdroid Line
  {
    id: "antdroid_beb", name: "Antdroid", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 1,
    lyt: 3, lyf: 5, deckLimit: 5,
    flavorText: "The most common robot Beb in the Unstable World, they only seek Anima and will eat without bias.",
  },
  {
    id: "antdroid_champion", name: "Antdroid Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 2,
    lyt: 3, lyf: 6, deckLimit: 4,
    flavorText: "Born on planet Alloith where oceans are molten metal, this Champion's body is comprised of this metal.",
  },
  {
    id: "antdroid_lord", name: "Antdroid Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3,
    lyt: 3, lyf: 7, deckLimit: 3,
    flavorText: "It is an Ant's duty to conquer. Since the initial unfolding of Time, they have, and until the final folding of time, they will.",
  },

  // ── DRAGON / DRAGON ───────────────────────────────────────
  // Lucky Dragon Line
  {
    id: "lucky_dragon_beb", name: "Lucky Dragon Beb", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 1,
    lyt: 7, lyf: 1, deckLimit: 5,
    flavorText: "Rarely seen, these Bebs are born in the Unstable World and have a fondness for Shade.",
  },
  {
    id: "lucky_dragon_champion", name: "Lucky Dragon Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 2,
    lyt: 6, lyf: 3, deckLimit: 4,
    flavorText: "Found only in barren wastelands of Shade, these dealers will grant almost any wish, but at what cost?",
  },
  {
    id: "lucky_dragon_lord", name: "Lucky Dragon Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3,
    lyt: 7, lyf: 3, deckLimit: 3,
    flavorText: "The Greatest Ever, Vavixzu the A'oeternis, was tricked by these Dragons.",
  },

  // Premier Dragon Line
  {
    id: "premier_dragon_beb", name: "Premier Dragon Beb", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 1,
    lyt: 4, lyf: 4, deckLimit: 5,
    flavorText: "The First & Only Leader of the Unstable World, when born, the surrounding area is briefly affected by Bloom.",
  },
  {
    id: "premier_dragon_champion", name: "Premier Dragon Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 2,
    lyt: 5, lyf: 4, deckLimit: 4,
    flavorText: "As the first dragon that Ascended to Champion, its power is only rivaled by its talent.",
  },
  {
    id: "premier_dragon_lord", name: "Premier Dragon Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3,
    lyt: 5, lyf: 5, deckLimit: 3,
    flavorText: "The First & Last, Zavixu the A'aeternis, deemed these dragons Worthy through rigorous trials.",
  },

  // Chaos Dragon Line
  {
    id: "chaos_dragon_beb", name: "Chaos Dragon Beb", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 1,
    lyt: 8, lyf: 0, deckLimit: 5,
    flavorText: "The One & Only Leader of the Unstable World, these Bebs bring with their birth a calamity of destruction in the form of Doom.",
  },
  {
    id: "chaos_dragon_champion", name: "Chaos Dragon Champion", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 2,
    lyt: 9, lyf: 0, deckLimit: 4,
    flavorText: "If ever you should be cursed enough to behold this innocent typhoon, know that escape is too late.",
  },
  {
    id: "chaos_dragon_lord", name: "Chaos Dragon Lord", type: CARD_TYPES.ESCENDANT,
    ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3,
    lyt: 10, lyf: 0, deckLimit: 3,
    flavorText: "The One & Only, Xavizu the A'oeternis, in a war with these dragons, admitted that they are a Threat.",
  },
];

// ============================================================
// IMPERA (Instincts — attached to beings from Luxmoire)
// ============================================================

export const IMPERA = [

  // ── H-01 Line ─────────────────────────────────────────────
  { id: "p_h01_1", name: "Natural Glow", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "h01_beb", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 1, khaoCost: 5,
    effectTag: "OP3", ssg: false,
    effect: "This being is Intangible." },
  { id: "p_h01_2", name: "Natural Lyt", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "h01_champion", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 2, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Choose 1 of your beings, it must be ruined before your adversary can attack another being." },
  { id: "p_h01_3", name: "Morning Star", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "h01_lord", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3, khaoCost: 5,
    effectTag: "OP3", ssg: false,
    effect: "Add 1 Astro Being to your hand from your Grimoire or Necropolis." },

  // ── H-09 Line ─────────────────────────────────────────────
  { id: "p_h09_1", name: "Cosmic Casbah", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "h09_beb", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 1, khaoCost: 4,
    effectTag: null, ssg: false,
    effect: "Your beings gain +1 Lyf, for every Astro Being you have in combat." },
  { id: "p_h09_2", name: "Binary Star", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "h09_champion", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 2, khaoCost: 9,
    effectTag: "OP3", ssg: false,
    effect: "Add 2 Astro Beings, up to Lvl 2, from your Grimoire or Necropolis to your hand." },
  { id: "p_h09_3", name: "Radiant Anima", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "h09_lord", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3, khaoCost: 7,
    effectTag: "OP2", ssg: false,
    effect: "Nullify 1 Lyt Attack." },

  // ── Xulan Line ────────────────────────────────────────────
  { id: "p_xulan_1", name: "Diamond Skin", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "xulan_beb", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 1, khaoCost: 3,
    effectTag: null, ssg: false,
    effect: "This being gains +2 Lyf." },
  { id: "p_xulan_2", name: "Natural Scalpel", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "xulan_champion", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 2, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "When this being Lyt Attacks, dim your adversary's Anima by 1." },
  { id: "p_xulan_3", name: "Eternal Nightmare", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "xulan_lord", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3, khaoCost: 8,
    effectTag: "OPT", ssg: false,
    effect: "Dim your own Anima by 4; nullify 1 attack on this being or its Soul." },

  // ── Spectral Tiger Line ───────────────────────────────────
  { id: "p_spectral_tiger_1", name: "Spectral Paws", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "spectral_tiger_beb", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 1, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This being's Lyt Attacks can be directed at your adversary's Anima instead of a being." },
  { id: "p_spectral_tiger_2", name: "Spectral Claws", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "spectral_tiger_champion", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 2, khaoCost: 6,
    effectTag: "OPT", ssg: false,
    effect: "If this being's Lyt Attack dimmed Anima this turn, dim their Anima by an additional 1." },
  { id: "p_spectral_tiger_3", name: "Haunting Presents", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "spectral_tiger_lord", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3, khaoCost: 10,
    effectTag: "OP2", ssg: false,
    effect: "Dim the Lyf of your adversary's cards by 2." },

  // ── Shadow Fox Line ───────────────────────────────────────
  { id: "p_shadow_fox_1", name: "Hidden Paws", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "shadow_fox_beb", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 1, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This being's Lyt Attacks can be directed at your adversary's Anima instead of a being." },
  { id: "p_shadow_fox_2", name: "Shadow Fur", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "shadow_fox_champion", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 2, khaoCost: 4,
    effectTag: "OP3", ssg: false,
    effect: "This being is Intangible." },
  { id: "p_shadow_fox_3", name: "Lord of Shadows", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "shadow_fox_lord", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3, khaoCost: 4,
    effectTag: "OP2", ssg: false,
    effect: "This being is Intangible." },

  // ── Inverted Serpent Line ─────────────────────────────────
  { id: "p_inverted_serpent_1", name: "Phantom Fangs", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "inverted_serpent_beb", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 1, khaoCost: 4,
    effectTag: "OPT", ssg: false,
    effect: "This being's Lyt Attacks can be directed at your adversary's Anima instead of a being." },
  { id: "p_inverted_serpent_2", name: "Supernatural Bite", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "inverted_serpent_champion", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 2, khaoCost: 6,
    effectTag: "OPT", ssg: false,
    effect: "Dim your adversary's Anima by 1, whenever this being Lyt Attacks." },
  { id: "p_inverted_serpent_3", name: "Inverted Scales", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "inverted_serpent_lord", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3, khaoCost: 8,
    effectTag: "OPT", ssg: false,
    effect: "Dim your adversary's Anima by 3, for every being they have that is currently Intangible." },

  // ── Z4Z Line ──────────────────────────────────────────────
  { id: "p_z4z_1", name: "Relentless", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "z4z_beb", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 1, khaoCost: 5,
    effectTag: "OP2", ssg: false,
    effect: "Dim your own Anima by 5; this being gains an additional Lyt Attack." },
  { id: "p_z4z_2", name: "Catastrophe", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "z4z_champion", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 2, khaoCost: 8,
    effectTag: "OP2", ssg: false,
    effect: "If you have another Lvl 2 Time Being, this being gains an additional Lyt Attack." },
  { id: "p_z4z_3", name: "Apocalypse", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "z4z_lord", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3, khaoCost: 8,
    effectTag: "OP3", ssg: false,
    effect: "Dim your own Anima by 8; your beings can all make an additional Lyt Attack." },

  // ── DG-0 Line ─────────────────────────────────────────────
  { id: "p_dg0_1", name: "Hope", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "dg0_beb", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 1, khaoCost: 5,
    effectTag: null, ssg: false,
    effect: "Gain +1 Anima, whenever a new Time Being enters combat." },
  { id: "p_dg0_2", name: "Law and Order", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "dg0_champion", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 2, khaoCost: 1,
    effectTag: "OP3", ssg: false,
    effect: "Dim your own Anima by 1; draw 1 card." },
  { id: "p_dg0_3", name: "Justice Prevails", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "dg0_lord", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3, khaoCost: 7,
    effectTag: "OPT", ssg: false,
    effect: "Draw 1 card, if you gained Anima this turn." },

  // ── Antdroid Line ─────────────────────────────────────────
  { id: "p_antdroid_1", name: "Restless", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "antdroid_beb", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 1, khaoCost: 5,
    effectTag: "OP3", ssg: false,
    effect: "Dim your own Anima by 5; this being may Lyt Attack again, but only to dim Anima." },
  { id: "p_antdroid_2", name: "Equivalent Exchange", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "antdroid_champion", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 2, khaoCost: 2,
    effectTag: "OP2", ssg: false,
    effect: "Dim your own Anima by 4; draw 2 cards." },
  { id: "p_antdroid_3", name: "King of the Anthill", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "antdroid_lord", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3, khaoCost: 10,
    effectTag: null, ssg: false,
    effect: "This being can make an additional Lyt Attack, for every other Antdroid being in combat." },

  // ── Lucky Dragon Line ─────────────────────────────────────
  { id: "p_lucky_dragon_1", name: "Pay Day", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "lucky_dragon_beb", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 1, khaoCost: 1,
    effectTag: "OP2", ssg: true,
    effect: "Dim your own Anima by 1. Flip a coin and choose your side; if it's your side, draw 1 card; if not, your adversary draws 1 as well." },
  { id: "p_lucky_dragon_2", name: "Black Cat's Best Friend", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "lucky_dragon_champion", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 2, khaoCost: 7,
    effectTag: "OP3", ssg: true,
    effect: "Flip a coin and choose your side; if it lands on your side, draw 2 cards, if not, your adversary draws 2 as well." },
  { id: "p_lucky_dragon_3", name: "Fate Maker", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "lucky_dragon_lord", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3, khaoCost: 7,
    effectTag: "OP3", ssg: true,
    effect: "If 1 of your cards is going to be Lyt Attacked, flip a coin and choose your side; if it lands on your side, reflect the Lyt Attack to your adversary's Anima." },

  // ── Premier Dragon Line ───────────────────────────────────
  { id: "p_premier_dragon_1", name: "Good Morning", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "premier_dragon_beb", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 1, khaoCost: 6,
    effectTag: null, ssg: false,
    effect: "This being gains +1 Lyt and +1 Lyf, for every Dragon Being you have in combat." },
  { id: "p_premier_dragon_2", name: "Social Dragonfly", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "premier_dragon_champion", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 2, khaoCost: 7,
    effectTag: null, ssg: false,
    effect: "This being gains +1 Lyt, for every Dragon Being in combat." },
  { id: "p_premier_dragon_3", name: "Destined for Greatness", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "premier_dragon_lord", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3, khaoCost: 10,
    effectTag: "OPT", ssg: false,
    effect: "This being is Intangible to Impera and U'hltis." },

  // ── Chaos Dragon Line ─────────────────────────────────────
  { id: "p_chaos_dragon_1", name: "Natural Disaster", type: CARD_TYPES.IMPERA, deckLimit: 5,
    owner: "chaos_dragon_beb", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 1, khaoCost: 9,
    effectTag: "OP2", ssg: false,
    effect: "Ruin 1 Non-Being." },
  { id: "p_chaos_dragon_2", name: "Thriving in Chaos", type: CARD_TYPES.IMPERA, deckLimit: 4,
    owner: "chaos_dragon_champion", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 2, khaoCost: 8,
    effectTag: "OPT", ssg: false,
    effect: "Dim your adversary's Anima by 1, whenever they Lyt Attack." },
  { id: "p_chaos_dragon_3", name: "None More Than I", type: CARD_TYPES.IMPERA, deckLimit: 3,
    owner: "chaos_dragon_lord", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3, khaoCost: 10,
    effectTag: null, ssg: false,
    effect: "Beings with more than 10 Lyt are instantly ruined and sent to Necropolis." },
];

// ============================================================
// U'HLTIS
// ============================================================

export const UHLTIS = [
  { id: "u_h09_lord", name: "Radiant Shower", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "h09_lord", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3, khaoCost: 6, animaCost: 2,
    effectTag: "OPT", effect: "Dim the Lyf of your adversary's cards by 1, or their Anima by 2." },
  { id: "u_xulan_lord", name: "Sweet Dreams", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "xulan_lord", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3, khaoCost: 4, animaCost: 6,
    effectTag: null, effect: "Dim your adversary's Anima by 1 whenever they Lyt Attack." },
  { id: "u_h01_lord", name: "Super Nova", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "h01_lord", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE, level: 3, khaoCost: 8, animaCost: 4,
    effectTag: "OP2", effect: "Dim your adversary's Anima by 4." },
  { id: "u_shadow_fox_lord", name: "Royal Darkness", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "shadow_fox_lord", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3, khaoCost: 4, animaCost: 6,
    effectTag: "OP2", effect: "Dim the Lyf of your adversary's cards by 2 and their Anima by 1." },
  { id: "u_spectral_tiger_lord", name: "Haunting Presence", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "spectral_tiger_lord", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3, khaoCost: 4, animaCost: 6,
    effectTag: "OP2", effect: "Dim the Lyf of your adversary's cards by 1 and their Anima by 2." },
  { id: "u_inverted_serpent_lord", name: "Inverted Fangs", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "inverted_serpent_lord", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM, level: 3, khaoCost: 4, animaCost: 4,
    effectTag: "OP2", effect: "If this being's Lyt Attack dimmed Anima this turn, dim their Anima by an additional 2." },
  { id: "u_antdroid_lord", name: "King of the Hill", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "antdroid_lord", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3, khaoCost: 7, animaCost: 8,
    effectTag: "OP2", effect: "All of your beings' Lyt Attacks can be directed at your adversary's Anima instead of a being." },
  { id: "u_dg0_lord", name: "Justice Reigns", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "dg0_lord", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3, khaoCost: 6, animaCost: 8,
    effectTag: "OP2", effect: "Send 1 Non-Being to Necropolis, or draw 1 card." },
  { id: "u_z4z_lord", name: "Apocalypse Now", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "z4z_lord", ilk: ILKS.MACHINE, school: SCHOOLS.TIME, level: 3, khaoCost: 5, animaCost: 6,
    effectTag: null, effect: "This being gains +1 Lyt for every Time Being in combat." },
  { id: "u_premier_dragon_lord", name: "Show Stomper", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "premier_dragon_lord", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3, khaoCost: 10, animaCost: 10,
    effectTag: "OP3", effect: "Dim your adversary's Anima by 10." },
  { id: "u_lucky_dragon_lord", name: "Dragon Eyes", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "lucky_dragon_lord", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3, khaoCost: 7, animaCost: 7,
    effectTag: "OP2", ssg: true, effect: "Roll 2 dice, if their sum is equal to 2, then you've just won 2 Shadow Gamblings, and you can activate another U'hlti." },
  { id: "u_chaos_dragon_lord", name: "I or None", type: CARD_TYPES.UHLTI, deckLimit: 1,
    owner: "chaos_dragon_lord", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON, level: 3, khaoCost: 10, animaCost: 10,
    effectTag: "OP3", effect: "Warlocks must ruin their own beings until each has only 1 being left." },
];

// ============================================================
// AFFINITIES
// ============================================================
// ── THE AXES, ALIGNED (caP'n, 2026-08-26) ─────────────────────
// `ilk` is the field every other card in the game carries its Ilk in —
// Humanoid/Beast/Machine/Dragon on a being, the SCHOOL string on a Tome
// (Omnia, Ritual, Contract…), "ally"/"wears"/"J'ynxi" on a Curio. An
// Affinity's Ilk is Holy / Evil / Awoken / J'ynxi and it now lives
// there, under the name the rest of the engine reads.
//
// ⚠ `affinityType` IS KEPT AS AN ALIAS, ON PURPOSE, AND CARRIES THE
// SAME VALUE. Five modules read it today (progression's affinity index,
// strikeLanguage's AFFINITY_TONES, cardLore, titles, the Debugger's card
// mirror) and a persisted collection can hand back an older card object.
// Removing it would be a rename dressed as an alignment. `ilk` is the
// CANON field from here on; `affinityType` is the compatibility name and
// is the one to drop once those five have moved.
//
// SCHOOL is NOT written here and must never be: `schoolOf` derives it
// from `type` and already answers "Affinity" for all four — the Axis
// Ruling's own reasoning, that a copied field is a field that can
// disagree with itself.

export const AFFINITIES = [
  { id: "aff_holy", name: "Y'Tyranuz", type: CARD_TYPES.AFFINITY, deckLimit: 1,
    ilk: AFFINITY_TYPES.HOLY, affinityType: AFFINITY_TYPES.HOLY, level: 4, khaoCost: 0,
    effectTag: "OP2", effect: "Nullify any 1 attack." },
  { id: "aff_awoken", name: "Enlightenment", type: CARD_TYPES.AFFINITY, deckLimit: 1,
    ilk: AFFINITY_TYPES.AWOKEN, affinityType: AFFINITY_TYPES.AWOKEN, level: 4, khaoCost: 0,
    effectTag: "OP2", effect: "Nullify 1 Affinity." },
  { id: "aff_evil", name: "X'aol", type: CARD_TYPES.AFFINITY, deckLimit: 1,
    ilk: AFFINITY_TYPES.EVIL, affinityType: AFFINITY_TYPES.EVIL, level: 4, khaoCost: 0,
    effectTag: "OP2", effect: "Send 1 card to Necropolis, except another Affinity." },
  { id: "aff_jynxi", name: "Fiend or Faux", type: CARD_TYPES.AFFINITY, deckLimit: 1,
    ilk: AFFINITY_TYPES.JYNXI, affinityType: AFFINITY_TYPES.JYNXI, level: 4, khaoCost: 0,
    effectTag: "OP2", ssg: true, effect: "Add 1 Shadow Gambling from your Grimoire or Necropolis to your hand; cannot play same Shadow Gambling twice in a row using this card." },
];

// ============================================================
// PROPHECIES
// ============================================================
export const PROPHECIES = [
  { id: "proph_space_race", name: "Space Race", type: CARD_TYPES.PROPHECY, level: "xxx", khaoCost: 15,
    effect: "If you have 2 Supreme Astro Beings and 1 A'oeternis or A'aeternis Space Being, you win the duel." },
  { id: "proph_phantom_planet", name: "Phantom Planet", type: CARD_TYPES.PROPHECY, level: 3, khaoCost: 15,
    effect: "If you have 2 Supreme Phantom Beings and 1 A'oeternis or A'aeternis Phantom Being, you win the duel." },
  { id: "proph_dragonforce", name: "Dragonforce", type: CARD_TYPES.PROPHECY, level: 2, khaoCost: 15,
    effect: "If you have 2 Supreme Dragon Beings and 1 A'oeternis or A'aeternis Dragon Being, you win the duel." },
  { id: "proph_time_to_pretend", name: "Time to Pretend", type: CARD_TYPES.PROPHECY, level: "xxx", khaoCost: 15,
    effect: "If you have 2 Supreme Time Beings and 1 A'oeternis or A'aeternis Time Being, you win the duel." },
  { id: "proph_tablet_omnis", name: "Tablet of O'mnis", type: CARD_TYPES.PROPHECY, level: 3, khaoCost: 15,
    effect: "If you have 2 Supreme Beings and 1 A'oeternis or A'aeternis, you win the duel." },
  { id: "proph_tablet_nullus", name: "Tablet of N'ullus", type: CARD_TYPES.PROPHECY, level: 2, khaoCost: 15,
    effect: "If your adversary has 2 Supreme Beings and 1 A'oeternis or A'aeternis, you win the duel." },
  { id: "proph_ace_of_shades", name: "Ace of Shades", type: CARD_TYPES.PROPHECY, level: 2, khaoCost: 13,
    effect: "If you win 7 Shadow Gamblings in a row, you win the duel." },
  { id: "proph_bloody_hell", name: "Bloody Hell", type: CARD_TYPES.PROPHECY, level: "xxx", khaoCost: 15,
    effect: "If a Warlock has lost 45 or more Anima, you win the duel." },
];

// ============================================================
// CURIOS
// ============================================================
export const CURIOS = [
  { id: "curio_wizards_cap", name: "Wizard's Cap", type: CARD_TYPES.CURIO, ilk: "wears", level: "xxx", khaoCost: 0, deckLimit: 1,
    effect: "If you are wearing headwear, you gain 3 Anima." },
  { id: "curio_circle_of_lyf", name: "Circle of Lyf", type: CARD_TYPES.CURIO, ilk: "wears", level: "xxx", khaoCost: 0, deckLimit: 1,
    effect: "If you are wearing a ring, you gain 1 Anima." },
  { id: "curio_jynxi_wares", name: "J'ynxi's Wares", type: CARD_TYPES.CURIO, ilk: "J'ynxi", level: "xxx", khaoCost: 0, deckLimit: 1,
    effect: "If you are wearing official J'ynxi Wares, you gain 2 Anima." },
  { id: "curio_spectacles", name: "Spectacular Spectacles", type: CARD_TYPES.CURIO, ilk: "wears", level: 1, khaoCost: 0, deckLimit: 1,
    effect: "If you are wearing glasses, you gain 1 Anima." },
  { id: "curio_mages_amulet", name: "Mage's Amulet", type: CARD_TYPES.CURIO, ilk: "wears", level: 2, khaoCost: 0, deckLimit: 1,
    effect: "If you are wearing a necklace, you gain 1 Anima." },
  { id: "curio_elixir", name: "Elixir of Health", type: CARD_TYPES.CURIO, ilk: "wears", level: 3, khaoCost: 0, deckLimit: 1,
    effect: "If you brought an elixir, you gain 1 Anima." },
  { id: "curio_warlocks_robe", name: "Warlock's Robe", type: CARD_TYPES.CURIO, ilk: "wears", level: 2, khaoCost: 0, deckLimit: 1,
    effect: "If you are wearing an outer garment, you gain 2 Anima." },
  { id: "curio_beb_buddy", name: "Beb Buddy", type: CARD_TYPES.CURIO, ilk: "ally", level: 2, khaoCost: 0, deckLimit: 1,
    effect: "If you have 1 inanimate companion at the duel table with you, add +1 to your Max Khao." },
  { id: "curio_badass_beb", name: "Badass Beb", type: CARD_TYPES.CURIO, ilk: "wears", level: 2, khaoCost: 0, deckLimit: 1,
    effect: "If your inanimate companion is wearing any sort of extra wear, gain 2 Anima." },
];

// ============================================================
// TOMES
// ============================================================
export const TOMES = [
  // Construction
  { id: "tome_khao_garden", name: "Khao Garden", type: CARD_TYPES.TOME, ilk: "Construction", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "OPT", effect: "Add +1 to your Max Khao at the end of the turn." },
  { id: "tome_khao_arcadia", name: "Khao Arcadia", type: CARD_TYPES.TOME, ilk: "Construction", level: 2, khaoCost: 4, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OP2", effect: "Add +2 to your Max Khao at the end of the turn." },
  { id: "tome_khao_paradise", name: "Khao Paradise", type: CARD_TYPES.TOME, ilk: "Construction", level: 3, khaoCost: 8, animaCost: 0, lyf: 1, deckLimit: 1, effectTag: "OP2", effect: "This card is Intangible the turn it was constructed. Add +2 to your Max Khao." },
  { id: "tome_bhodi_tree", name: "Bhodi Tree", type: CARD_TYPES.TOME, ilk: "Construction", level: 1, khaoCost: 1, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "OPT", effect: "You gain +1 Anima." },
  { id: "tome_anima_house", name: "Anima House", type: CARD_TYPES.TOME, ilk: "Construction", level: 2, khaoCost: 2, animaCost: 0, lyf: 1, deckLimit: 2, effectTag: "OP2", effect: "You gain +2 Anima." },
  { id: "tome_stars_and_skies", name: "The Stars and Skies", type: CARD_TYPES.TOME, ilk: "Construction", level: 3, khaoCost: 8, animaCost: 0, lyf: 1, deckLimit: 1, effectTag: "OP3", effect: "This Tome is Intangible the turn it was constructed. You gain +5 Anima." },
  { id: "tome_khao_duhmdumn_beb", name: "Khao D'uhmdumn Beb", type: CARD_TYPES.TOME, ilk: "Construction", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: null, effect: "Your adversary must ruin this card before attacking another." },
  { id: "tome_khao_duhmdumn_champion", name: "Khao D'uhmdumn Champion", type: CARD_TYPES.TOME, ilk: "Construction", level: 2, khaoCost: 4, animaCost: 0, lyf: 1, deckLimit: 2, effectTag: null, effect: "Your adversary must ruin this card before attacking another." },
  { id: "tome_khao_duhmdumn_lord", name: "Khao D'uhmdumn Lord", type: CARD_TYPES.TOME, ilk: "Construction", level: 3, khaoCost: 6, animaCost: 0, lyf: 1, deckLimit: 1, effectTag: null, effect: "Your adversary must ruin this card before attacking another; the first attack on this card is nullified." },

  // Treasure
  { id: "tome_necklace_death", name: "Necklace of Congealed Death", type: CARD_TYPES.TOME, ilk: "Treasure", level: 1, khaoCost: 2, animaCost: 0, lyf: 2, deckLimit: 3, effectTag: "ATV", effect: "Feed this Treasure up to 4 Anima per turn; after feeding it 8 Anima, ruin 1 being and its Soul." },
  { id: "tome_necklace_courage", name: "Necklace of Joyous Courage", type: CARD_TYPES.TOME, ilk: "Treasure", level: 2, khaoCost: 3, animaCost: 0, lyf: 3, deckLimit: 2, effectTag: "ATV", effect: "Feed this Treasure up to 3 Khao per turn; after feeding it 6 Khao, gain 6 Anima." },
  { id: "tome_necklace_akula", name: "Necklace of Akula", type: CARD_TYPES.TOME, ilk: "Treasure", level: 3, khaoCost: 3, animaCost: 0, lyf: 3, deckLimit: 1, effectTag: null, effect: "This Treasure is Intangible for the first 3 turns. You gain +1 Anima whenever a being is ruined." },
  { id: "tome_lightning_blade", name: "Lightning Blade", type: CARD_TYPES.TOME, ilk: "Treasure", level: 1, khaoCost: 8, animaCost: 0, lyf: 1, deckLimit: 3, effectTag: null, effect: "This Treasure belongs to 1 being; when the wielder Lyt Attacks, dim the Lyf of your adversary's cards by 1." },
  { id: "tome_sword_of_shadows", name: "Sword of Shadows", type: CARD_TYPES.TOME, ilk: "Treasure", level: 2, khaoCost: 6, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OPT", effect: "This Treasure belongs to 1 being. The wielder gains an additional Lyt Attack." },
  { id: "tome_weeping_dagger", name: "Weeping Dagger", type: CARD_TYPES.TOME, ilk: "Treasure", level: 3, khaoCost: 10, animaCost: 0, lyf: 3, deckLimit: 1, effectTag: "OP3", effect: "Ruin 1 being and its Soul." },
  { id: "tome_gauntlets_azka", name: "Gauntlets of A'zka", type: CARD_TYPES.TOME, ilk: "Treasure", level: 1, khaoCost: 2, animaCost: 0, lyf: 2, deckLimit: 3, effectTag: null, effect: "This Treasure belongs to 1 being; its wielder gains +2 Lyt." },
  { id: "tome_boots_wing_walking", name: "Boots of Wing Walking", type: CARD_TYPES.TOME, ilk: "Treasure", level: 2, khaoCost: 6, animaCost: 0, lyf: 1, deckLimit: 2, effectTag: "OPT", effect: "Make 1 of your beings Intangible." },
  { id: "tome_chief_calidum", name: "Chief Calidum's Cuirass", type: CARD_TYPES.TOME, ilk: "Treasure", level: 3, khaoCost: 3, animaCost: 0, lyf: 3, deckLimit: 1, effectTag: "OP3", effect: "Transfer this card's Lyf to any 1 card, that has Lyf, for 3 turns." },

  // Nihil
  { id: "tome_rays_of_hope", name: "Rays of Hope", type: CARD_TYPES.TOME, ilk: "Nihil", level: 1, khaoCost: 3, animaCost: 0, deckLimit: 3, effectTag: null, effect: "Nullify the effect, or activation, of 1 of your adversary's cards, except a Nihil Tome, for the rest of the turn; if it's an Omnia Tome, ruin it." },
  { id: "tome_cleansing_burst", name: "Cleansing Burst of Nihil", type: CARD_TYPES.TOME, ilk: "Nihil", level: 2, khaoCost: 5, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Nullify the effect, or activation, of 1 of your adversary's cards, except a Nihil Tome, for the rest of the turn; if it's an Omnia Tome or Curios, ruin it." },
  { id: "tome_divine_justice", name: "Divine Justice", type: CARD_TYPES.TOME, ilk: "Nihil", level: 3, khaoCost: 6, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Nullify the effect, or activation, of all Omnia Tomes for 2 turns." },
  { id: "tome_shower_of_lyt", name: "Shower of Lyt", type: CARD_TYPES.TOME, ilk: "Nihil", level: 1, khaoCost: 3, animaCost: 0, deckLimit: 3, effectTag: null, effect: "Your beings cannot be attacked using Tomes this turn." },
  { id: "tome_radiant_aura", name: "Radiant Aura", type: CARD_TYPES.TOME, ilk: "Nihil", level: 2, khaoCost: 6, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Nullify 1 attack being made to 1 of your cards, if the attacking card is an Omnia Tome, ruin it." },
  { id: "tome_phoenix_aura", name: "Phoenix Aura", type: CARD_TYPES.TOME, ilk: "Nihil", level: 3, khaoCost: 8, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Your cards cannot be attacked using Tomes for 2 turns." },
  { id: "tome_wall_of_light", name: "Wall of Light", type: CARD_TYPES.TOME, ilk: "Nihil", level: 1, khaoCost: 5, animaCost: 0, deckLimit: 3, effectTag: null, effect: "If your Anima would be dimmed this turn, absorb the damage and add it to your Anima." },
  { id: "tome_lyt_footed", name: "Lyt Footed", type: CARD_TYPES.TOME, ilk: "Nihil", level: 2, khaoCost: 4, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Nullify 1 attack being made to 1 of your beings, if the attacking card is an Omnia Tome, ruin it." },
  { id: "tome_sword_shield_nihil", name: "Sword and Shield of Nihil", type: CARD_TYPES.TOME, ilk: "Nihil", level: 3, khaoCost: 6, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Nullify 1 attack being made to 1 of your cards, if the attacking card was a Tome, ruin it." },

  // Omnia
  { id: "tome_nothing_left", name: "Nothing Left", type: CARD_TYPES.TOME, ilk: "Omnia", level: 1, khaoCost: 3, animaCost: 1, deckLimit: 3, effectTag: null, effect: "Dim your own Anima by 1; ruin 1 Tome or Curios." },
  { id: "tome_regnant", name: "Regnant", type: CARD_TYPES.TOME, ilk: "Omnia", level: 2, khaoCost: 4, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Ruin 1 Tome or Curios." },
  { id: "tome_all_or_nothing", name: "All or Nothing", type: CARD_TYPES.TOME, ilk: "Omnia", level: 3, khaoCost: 6, animaCost: 0, deckLimit: 1, effectTag: null, effect: "If your adversary attempts to activate a Tome, or one is in use, ruin it instead." },
  { id: "tome_radiant_armor", name: "Radiant Armor", type: CARD_TYPES.TOME, ilk: "Omnia", level: 1, khaoCost: 2, animaCost: 1, deckLimit: 3, effectTag: null, effect: "Dim your own Anima by 1; ruin 1 Impera." },
  { id: "tome_omnis_corruption", name: "Omnis' Corruption Jet", type: CARD_TYPES.TOME, ilk: "Omnia", level: 2, khaoCost: 4, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Ruin 1 Impera or U'hlti." },
  { id: "tome_superque", name: "Superque", type: CARD_TYPES.TOME, ilk: "Omnia", level: 3, khaoCost: 8, animaCost: 1, deckLimit: 1, effectTag: null, effect: "Dim your own Anima by 1; ruin 1 Impera and 1 U'hlti." },
  { id: "tome_wall_of_life", name: "Wall of Life", type: CARD_TYPES.TOME, ilk: "Omnia", level: 1, khaoCost: 5, animaCost: 0, deckLimit: 3, effectTag: null, effect: "Ruin 1 Non-Being." },
  { id: "tome_lyt_hearted", name: "Lyt Hearted", type: CARD_TYPES.TOME, ilk: "Omnia", level: 2, khaoCost: 4, animaCost: 1, deckLimit: 2, effectTag: null, effect: "Dim your own Anima by 1; ruin 1 card." },
  { id: "tome_sword_shield_omnis", name: "Sword and Shield of O'mnis", type: CARD_TYPES.TOME, ilk: "Omnia", level: 3, khaoCost: 8, animaCost: 0, deckLimit: 1, effectTag: null, effect: "If your adversary attempts to activate a card effect, or one is in use, ruin it instead." },

  // Ancient Sorcery
  { id: "tome_not_here", name: "Not Here", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 1, khaoCost: 4, animaCost: 0, deckLimit: 3, effectTag: null, effect: "Add 1 lvl 1 Tome from your Grimoire to your hand." },
  { id: "tome_not_now", name: "Not Now", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 2, khaoCost: 6, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Add 1 Tome, up to lvl 2, from your Grimoire to your hand." },
  { id: "tome_not_ever", name: "Not Ever!", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 3, khaoCost: 8, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Add 1 Tome, up to lvl 3, from your Grimoire to your hand." },
  { id: "tome_blood_in_blood_out", name: "Blood in Blood Out", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 1, khaoCost: 4, animaCost: 0, deckLimit: 3, effectTag: null, effect: "Send 1 being from your hand or Grimoire to Necropolis, then add 1 Lvl 1 being from your Grimoire to your hand." },
  { id: "tome_blood_money", name: "Blood Money", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 2, khaoCost: 5, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Send 1 card from your hand or Grimoire to Necropolis, then add 1 being up to Lvl 2 from your Grimoire to your hand." },
  { id: "tome_river_of_blood", name: "River of Blood", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 3, khaoCost: 6, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Send 1 being from your hand or Grimoire to Necropolis, then add 1 being, up to Lvl 3, from your Grimoire to your hand." },
  { id: "tome_shadow_blanket", name: "Shadow Blanket", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 1, khaoCost: 3, animaCost: 0, deckLimit: 3, effectTag: null, effect: "Search your Luxmoire for a lvl 2 Impera and add it to your hand." },
  { id: "tome_inverted_knife", name: "Inverted Knife", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 2, khaoCost: 5, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Add 1 U'hlti from your Grimoire to your hand." },
  { id: "tome_light_casting_shadow", name: "Light Casting Shadow", type: CARD_TYPES.TOME, ilk: "Ancient Sorcery", level: 3, khaoCost: 8, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Add 1 Impera from your Luxmoire and 1 U'hlti from your Grimoire to your hand." },

  // Ritual
  { id: "tome_seal_of_omnis", name: "Seal of Omnis", type: CARD_TYPES.TOME, ilk: "Ritual", level: 1, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "OPT", effect: "Add 1 lvl 1 Tome, or 1 Curios from Necropolis to your hand, except a Ritual Tome." },
  { id: "tome_monoliths_of_omnis", name: "Monoliths of Omnis", type: CARD_TYPES.TOME, ilk: "Ritual", level: 2, khaoCost: 7, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OP2", effect: "Add 1 Tome up to lvl 2, or 1 Curios from Necropolis to your hand, except a Ritual Tome." },
  { id: "tome_mausoleum_of_omnis", name: "Mausoleum of Omnis", type: CARD_TYPES.TOME, ilk: "Ritual", level: 3, khaoCost: 9, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: "OP2", effect: "Add 1 Tome, up to lvl 3, or 1 Curios from Necropolis to your hand, except a Ritual Tome." },
  { id: "tome_seal_of_nihil", name: "Seal of Nihil", type: CARD_TYPES.TOME, ilk: "Ritual", level: 1, khaoCost: 2, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "OPT", effect: "Add 1 lvl 1 Impera from Necropolis to your hand." },
  { id: "tome_monoliths_of_nihil", name: "Monoliths of Nihil", type: CARD_TYPES.TOME, ilk: "Ritual", level: 2, khaoCost: 4, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OP2", effect: "Add 1 Impera, up to lvl 2, from Necropolis to your hand." },
  { id: "tome_mausoleum_of_nihil", name: "Mausoleum of Nihil", type: CARD_TYPES.TOME, ilk: "Ritual", level: 3, khaoCost: 6, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: "OPT", effect: "Add 1 Impera, up to lvl 3, or U'hlti card from Necropolis to your hand." },
  { id: "tome_soulcerers_seal", name: "Soulcerer's Seal", type: CARD_TYPES.TOME, ilk: "Ritual", level: 1, khaoCost: 3, animaCost: 0, lyf: 0, deckLimit: 3, effectTag: "OPT", effect: "Resurrect 1 lvl 1 being from Necropolis." },
  { id: "tome_soulcerers_monoliths", name: "Soulcerer's Monoliths", type: CARD_TYPES.TOME, ilk: "Ritual", level: 2, khaoCost: 5, animaCost: 0, lyf: 0, deckLimit: 2, effectTag: "OP2", effect: "Resurrect 1 being, up to lvl 2, from Necropolis." },
  { id: "tome_soulcerers_mausoleum", name: "Soulcerer's Mausoleum", type: CARD_TYPES.TOME, ilk: "Ritual", level: 3, khaoCost: 7, animaCost: 0, lyf: 0, deckLimit: 1, effectTag: "OP2", effect: "Resurrect 1 being, up to lvl 3, or 2 lvl 1 beings." },

  // Shadow Gambling
  { id: "tome_temptation_manifested", name: "Temptation Manifested", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 0, animaCost: 0, deckLimit: 1, effectTag: "ACB",
    effect: "ACB 1-6: Roll 1 dice. If you roll the chosen number, add any 1 card from Necropolis to your hand; if wrong, your adversary wins the Shadow Gambling." },
  { id: "tome_snake_eyes", name: "Snake Eyes", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 4, animaCost: 0, deckLimit: 1, effectTag: "ACB",
    effect: "ACB 1-6: Roll 2 dice. If they are both the chosen number, add any 2 cards from your Grimoire to your hand." },
  { id: "tome_nevermore", name: "Nevermore", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 0, animaCost: 0, deckLimit: 1, effectTag: "ACB",
    effect: "ACB 1-6: Roll 1 dice. If you roll the chosen number, add any 1 card from your Grimoire to your hand; if wrong, your adversary wins the Shadow Gambling." },
  { id: "tome_underdogs_howl", name: "Underdog's Howl", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 7, animaCost: 0, deckLimit: 1, effectTag: null,
    effect: "Pick 1 card from your Grimoire and put it up for bet; if you win your next Shadow Gambling, add that card to your hand, otherwise, send it to Necropolis." },
  { id: "tome_daft_fantasy", name: "Daft Fantasy", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 6, animaCost: 0, deckLimit: 1, effectTag: null,
    effect: "Pick a card with a lvl 1-3 from your Grimoire, your adversary gets 1 try to guess its Lvl; if correct, add it to your hand; if not, your adversary wins the Shadow Gambling." },
  { id: "tome_goodbye_horses", name: "Goodbye Horses", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 8, animaCost: 0, deckLimit: 1, effectTag: null,
    effect: "Pick 2 Tomes from your Grimoire, your adversary guesses what Ilk they each are, you keep all wrong choices." },
  { id: "tome_casino_royale", name: "Casino Royale", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 10, animaCost: 0, deckLimit: 1, effectTag: null,
    effect: "Flip a coin and choose your side. If it's your side, your next Shadow Gambling, except Casino Royale, is an automatic win in your favor." },
  { id: "tome_dragon_deyes", name: "Dragon D'eyes", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 7, animaCost: 0, deckLimit: 1, effectTag: "ACB",
    effect: "ACB 1-6: Roll 2 dice. You draw 1 card for every incorrect match; your adversary draws 1 card for every correct match." },
  { id: "tome_gambits_lucky_cat", name: "Gambit's Lucky Cat", type: CARD_TYPES.TOME, ilk: "Shadow Gambling", level: 3, khaoCost: 7, animaCost: 0, deckLimit: 1, effectTag: null,
    effect: "Flip a coin and choose your side. If it's your side, add any 2 cards from your Grimoire or Necropolis to your hand; if not your side, your adversary wins the Shadow Gambling." },

  // Contract
  { id: "tome_devils_advocate", name: "The Devil's Advocate", type: CARD_TYPES.TOME, ilk: "Contract", level: 1, khaoCost: 1, animaCost: 0, deckLimit: 3, effectTag: null, effect: "You and your adversary draw 1 card each." },
  { id: "tome_monkey_suite", name: "Monkey Suite", type: CARD_TYPES.TOME, ilk: "Contract", level: 2, khaoCost: 3, animaCost: 0, deckLimit: 2, effectTag: null, effect: "You and your adversary gain 5 Anima each." },
  { id: "tome_judge_jury", name: "Judge, Jury, Executioner", type: CARD_TYPES.TOME, ilk: "Contract", level: 3, khaoCost: 6, animaCost: 0, deckLimit: 1, effectTag: null, effect: "You and your adversary both add 1 Blood Contract to your hand from Necropolis, but yours can also be from your Grimoire." },
  { id: "tome_restraining_ordeal", name: "Restraining Ordeal", type: CARD_TYPES.TOME, ilk: "Contract", level: 1, khaoCost: 3, animaCost: 0, deckLimit: 3, effectTag: "ATV", effect: "Every turn your adversary doesn't Lyt Attack you, they gain 1 Anima. You gain 2 Anima when this card is ruined." },
  { id: "tome_agree_to_disagree", name: "Agree to Disagree", type: CARD_TYPES.TOME, ilk: "Contract", level: 2, khaoCost: 7, animaCost: 0, deckLimit: 2, effectTag: null, effect: "Your adversary must choose 1 of the following: you draw 1 card and your adversary gains 5 Anima, or you draw 2 cards and they draw 1." },
  { id: "tome_solomon_godfry", name: "Solomon Godfry's Manslaughter", type: CARD_TYPES.TOME, ilk: "Contract", level: 3, khaoCost: 8, animaCost: 0, deckLimit: 1, effectTag: null, effect: "Both Warlocks must ruin 1 of their own beings and its Soul." },
  { id: "tome_magma_carta", name: "Magma Carta", type: CARD_TYPES.TOME, ilk: "Contract", level: 1, khaoCost: 8, animaCost: 0, deckLimit: 3, effectTag: "OPT", effect: "Dim your own Anima by 4, then draw 1 card. This card is ruined once your adversary devotes to you 4 Anima in total." },
  { id: "tome_seize_and_disease", name: "Seize and Disease", type: CARD_TYPES.TOME, ilk: "Contract", level: 2, khaoCost: 4, animaCost: 0, deckLimit: 2, effectTag: null, effect: "You and your adversary choose 1 of each other's Tomes to ruin." },
  { id: "tome_king_of_the_castle", name: "King of the Castle", type: CARD_TYPES.TOME, ilk: "Contract", level: 3, khaoCost: 10, animaCost: 0, deckLimit: 1, effectTag: "OPT", effect: "Your adversary must devote to you at least 2 of their Anima. This card is ruined once your adversary devotes to you 8 Anima in total. Draw 1 card when this card is sent to Necropolis." },
];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

// Get all cards that can go in a Grimoire

export const getAllGrimoireCards = () => [
  ...ESCENDANTS,
  ...TOMES,
  ...CURIOS,
  ...UHLTIS,
  ...AFFINITIES,
  ...PROPHECIES,
];

// Get all Impera (Luxmoire only)
export const getAllImpera = () => [...IMPERA];

// Get Impera for a specific being
export const getImperaForBeing = (escendantId) =>
  IMPERA.filter(p => p.owner === escendantId);

// Get U'hlti for a specific being
export const getUhltiForBeing = (escendantId) =>
  UHLTIS.find(u => u.owner === escendantId) || null;

// Get Escendants by school and ilk
export const getEscendantsBySchoolAndIlk = (cls, ilk) =>
  ESCENDANTS.filter(e => e.school === cls && e.ilk === ilk);

// Get the full line (beb, champion, lord) for an escendant
export const getEscendantLine = (escendantId) => {
  const card = ESCENDANTS.find(e => e.id === escendantId);
  if (!card) return [];
  return ESCENDANTS.filter(e => e.school === card.school && e.ilk === card.ilk)
    .sort((a, b) => a.level - b.level);
};

// Check if a being can naturally ascend to next level
export const canNaturallyAscend = (currentId, targetId) => {
  const current = ESCENDANTS.find(e => e.id === currentId);
  const target = ESCENDANTS.find(e => e.id === targetId);
  if (!current || !target) return false;
  return current.school === target.school &&
    current.ilk === target.ilk &&
    target.level === current.level + 1;
};

// Get escling for a given school/ilk
export const getEsclingForSchoolIlk = (ilk) =>
  ESCLINGS.find(e => e.ilk === ilk) || null;
// ── SCHOOL FOR A SUPPORT, DERIVED (ruling of 2026-08-25) ──────
// For a BEING, School is a stored field: the Family-B stamp word in
// `card.school`. For a TOME, U'hlti, Impera, Affinity or Curio, the
// designer's ruling is that School is "the broad Aenergy-Art term" —
// Tome, U'hlti, Impera, Affinity — which is precisely what those rows
// already carry in `card.type`. So it is DERIVED here rather than
// written onto 100+ rows: duplicated data is data that can disagree
// with itself, and there is nothing a stored copy could say that this
// one line cannot.
//
// (Their `ilk` stays the SCHOOL string the rows already hold — Omnia,
// Ritual, Contract, Nihil, Construction, Treasure, Ancient Sorcery,
// Shadow Gambling, and the Curio tags. That was already correct and
// was not touched by the 2026-08-25 field-name swap. AFFINITIES joined
// them on 2026-08-26: their Ilk is Holy/Evil/Awoken/J'ynxi and it is
// now on `ilk` like everybody else's — see the banner over AFFINITIES.)
//
// Prophecies and Curios have no Aenergy Art; for them this returns
// their type too, which is the most honest thing available and is
// clearly labelled as such rather than being invented.

export const schoolOf = (card) => {
  if (!card) return null;
  if (card.type === CARD_TYPES.ESCENDANT || card.type === CARD_TYPES.ESCLING) {
    return card.school ?? null;
  }
  return card.type ?? null;
};
